import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  FileText, 
  Video, 
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  ClipboardList
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/dashboard/stats`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [API_URL, token]);

  const statCards = [
    {
      title: 'Total Leads',
      value: stats?.total_leads || 0,
      icon: Users,
      color: 'bg-blue-100 text-blue-600',
      link: '/admin/leads'
    },
    {
      title: 'New Leads',
      value: stats?.new_leads || 0,
      icon: AlertCircle,
      color: 'bg-amber-100 text-amber-600',
      link: '/admin/leads'
    },
    {
      title: 'Booked',
      value: stats?.booked_leads || 0,
      icon: CheckCircle,
      color: 'bg-green-100 text-green-600',
      link: '/admin/leads'
    },
    {
      title: 'Blog Posts',
      value: stats?.total_posts || 0,
      icon: FileText,
      color: 'bg-purple-100 text-purple-600',
      link: '/admin/blog'
    },
    {
      title: 'Videos',
      value: stats?.total_videos || 0,
      icon: Video,
      color: 'bg-pink-100 text-pink-600',
      link: '/admin/videos'
    },
    {
      title: 'Contacted',
      value: stats?.contacted_leads || 0,
      icon: Clock,
      color: 'bg-sage-100 text-sage-600',
      link: '/admin/leads'
    },
    {
      title: 'Nutrition Apps',
      value: stats?.total_nutrition_apps || 0,
      icon: ClipboardList,
      color: 'bg-teal-100 text-teal-600',
      link: '/admin/nutrition-apps'
    },
    {
      title: 'New Apps',
      value: stats?.new_nutrition_apps || 0,
      icon: AlertCircle,
      color: 'bg-orange-100 text-orange-600',
      link: '/admin/nutrition-apps'
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-sage-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="admin-dashboard-error" className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="w-12 h-12 text-charcoal/20 mb-4" />
        <h2 className="font-serif text-2xl text-charcoal mb-2">Unable to Load Dashboard</h2>
        <p className="text-charcoal/60 mb-6">Something went wrong while fetching data.</p>
        <Button
          onClick={() => window.location.reload()}
          variant="outline"
          className="rounded-xl"
        >
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-light text-charcoal mb-2">Dashboard</h1>
        <p className="text-charcoal/60">Welcome back! Here's an overview of your website.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Link to={stat.link}>
              <Card className="bg-white border-0 shadow-soft hover:shadow-medium transition-all rounded-xl overflow-hidden group">
                <CardContent className="p-5">
                  <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mb-3`}>
                    <stat.icon className="w-5 h-5" />
                  </div>
                  <div className="font-serif text-3xl text-charcoal mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-charcoal/60">
                    {stat.title}
                  </div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Link to="/admin/leads">
          <Card className="bg-white border-0 shadow-soft hover:shadow-medium transition-all rounded-xl group">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-charcoal">Manage Leads</h3>
                <Users className="w-5 h-5 text-sage-500" />
              </div>
              <p className="text-sm text-charcoal/60 mb-4">
                View and manage contact form submissions
              </p>
              <span className="text-sm text-sage-600 font-medium group-hover:underline">
                View all leads &rarr;
              </span>
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/blog">
          <Card className="bg-white border-0 shadow-soft hover:shadow-medium transition-all rounded-xl group">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-charcoal">Manage Blog</h3>
                <FileText className="w-5 h-5 text-sage-500" />
              </div>
              <p className="text-sm text-charcoal/60 mb-4">
                Create and edit blog posts
              </p>
              <span className="text-sm text-sage-600 font-medium group-hover:underline">
                Manage posts &rarr;
              </span>
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/videos">
          <Card className="bg-white border-0 shadow-soft hover:shadow-medium transition-all rounded-xl group">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-charcoal">Manage Videos</h3>
                <Video className="w-5 h-5 text-sage-500" />
              </div>
              <p className="text-sm text-charcoal/60 mb-4">
                Add and manage video content
              </p>
              <span className="text-sm text-sage-600 font-medium group-hover:underline">
                Manage videos &rarr;
              </span>
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/nutrition-apps">
          <Card className="bg-white border-0 shadow-soft hover:shadow-medium transition-all rounded-xl group">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-charcoal">Nutrition Applications</h3>
                <ClipboardList className="w-5 h-5 text-sage-500" />
              </div>
              <p className="text-sm text-charcoal/60 mb-4">
                Review nutritional therapy applications
              </p>
              <span className="text-sm text-sage-600 font-medium group-hover:underline">
                View applications &rarr;
              </span>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
